import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  Check,
  CheckCircle2,
  Megaphone,
  Play,
  RefreshCw,
  Sparkles,
  Trash2,
} from 'lucide-react';

import { listEmployees } from '@/api/employees.api';
import { archiveHeadline, createHeadline, type Headline } from '@/api/eos/headlines.api';
import { updateIssue, type Issue } from '@/api/eos/issues.api';
import {
  advanceSegment,
  concludeMeeting,
  getMeetingWorkspace,
  setAttendees,
  startMeeting,
  submitRating,
  type AttendanceEntry,
  type MeetingRecap,
  type MeetingWorkspace,
  type WorkspaceMeeting,
  type WorkspaceMetric,
} from '@/api/eos/meetings.api';
import { updateRock, type Rock, type RockStatus } from '@/api/eos/rocks.api';
import { carryOverTodo, completeTodo, createTodo } from '@/api/eos/todos.api';
import { RockStatusBadge } from '@/components/eos/RockStatusBadge';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Modal } from '@/components/ui/Modal';
import { cn } from '@/lib/cn';
import { describeEosError } from '@/lib/eos/errors';
import { ROCK_STATUS_META } from '@/lib/eos/rockStatus';
import type { MetricStatus } from '@/lib/scorecards/status';
import { isSuperadmin } from '@/lib/capabilities';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';

/**
 * The live L10 runner — Stream B Wave 2 (ADR-023).
 *
 * One workspace read polls every 5s (no websocket in V1); a 1-second
 * client tick derives the timebox countdown from `segmentStartedAt` +
 * the agenda minutes. Segment moves, attendance, and conclude are
 * facilitator-only (server-enforced — the UI only hides the controls);
 * everything inside a segment (rocks, to-dos, IDS, headlines) is the
 * regular member-level EOS surface scoped to this meeting's team.
 */

/** on_track → off_track → done → on_track (same cycle as RocksPage). */
const NEXT_ROCK_STATUS: Record<RockStatus, RockStatus> = {
  on_track: 'off_track',
  off_track: 'done',
  done: 'on_track',
};

const METRIC_STATUS_TONE: Record<MetricStatus, string> = {
  green: 'bg-kw-emerald-light text-kw-tag-text',
  yellow: 'bg-kw-warning-bg text-kw-warning-text',
  red: 'bg-kw-danger-bg text-kw-danger-text',
};

function formatMeetingTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatClock(ms: number): string {
  const total = Math.floor(Math.abs(ms) / 1000);
  return `${String(Math.floor(total / 60))}:${String(total % 60).padStart(2, '0')}`;
}

export function MeetingRunnerPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { state: authState } = useAuth();

  const me = authState.status === 'authenticated' ? authState.me : null;
  const myMembershipId = me?.membership.id ?? null;
  const isSuper = me !== null && isSuperadmin(me);

  const workspaceQuery = useQuery({
    queryKey: ['eos', 'meetings', id, 'workspace'],
    queryFn: () => getMeetingWorkspace(id),
    enabled: id !== '',
    // Poll only while the meeting is live — the runner is multiplayer.
    refetchInterval: (query) => (query.state.data?.meeting.status === 'in_progress' ? 5000 : false),
  });

  // 1s tick for the timebox countdown, only while in progress.
  const status = workspaceQuery.data?.meeting.status;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (status !== 'in_progress') return undefined;
    const timer = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => {
      window.clearInterval(timer);
    };
  }, [status]);

  const [recap, setRecap] = useState<MeetingRecap | null>(null);
  const [solving, setSolving] = useState<Issue | null>(null);

  function invalidate(): void {
    void queryClient.invalidateQueries({ queryKey: ['eos', 'meetings', id, 'workspace'] });
  }
  function toastError(fallback: string) {
    return (err: unknown) => {
      toast.show(describeEosError(err, fallback), { variant: 'danger' });
    };
  }

  const startMutation = useMutation({
    mutationFn: () => startMeeting(id),
    onSuccess: invalidate,
    onError: toastError('Could not start the meeting.'),
  });
  const advanceMutation = useMutation({
    mutationFn: (toKey?: string) => advanceSegment(id, toKey),
    onSuccess: invalidate,
    onError: toastError('Could not change the segment.'),
  });
  const attendeesMutation = useMutation({
    mutationFn: (entries: AttendanceEntry[]) => setAttendees(id, entries),
    onSuccess: invalidate,
    onError: toastError('Could not update attendance.'),
  });
  const ratingMutation = useMutation({
    mutationFn: (rating: number) => submitRating(id, rating),
    onSuccess: invalidate,
    onError: toastError('Could not submit your rating.'),
  });
  const concludeMutation = useMutation({
    mutationFn: (summaryText?: string) => concludeMeeting(id, summaryText),
    onSuccess: (data) => {
      setRecap(data.recap);
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ['eos', 'meetings'] });
    },
    onError: toastError('Could not conclude the meeting.'),
  });

  const rockStatusMutation = useMutation({
    mutationFn: ({ rockId, rockStatus }: { rockId: string; rockStatus: RockStatus }) =>
      updateRock(rockId, { status: rockStatus }),
    onSuccess: invalidate,
    onError: toastError('Could not update the rock status.'),
  });
  const completeTodoMutation = useMutation({
    mutationFn: (todoId: string) => completeTodo(todoId),
    onSuccess: invalidate,
    onError: toastError('Could not complete the to-do.'),
  });
  const carryOverMutation = useMutation({
    mutationFn: (todoId: string) => carryOverTodo(todoId),
    onSuccess: () => {
      invalidate();
      toast.show('Carried to next week', { variant: 'success' });
    },
    onError: toastError('Could not carry the to-do over.'),
  });
  const addHeadlineMutation = useMutation({
    mutationFn: (title: string) =>
      createHeadline({
        title,
        meetingId: id,
        departmentId: workspaceQuery.data?.meeting.departmentId ?? undefined,
      }),
    onSuccess: invalidate,
    onError: toastError('Could not share the headline.'),
  });
  const archiveHeadlineMutation = useMutation({
    mutationFn: (headlineId: string) => archiveHeadline(headlineId),
    onSuccess: invalidate,
    onError: toastError('Could not archive the headline.'),
  });
  const solveIssueMutation = useMutation({
    mutationFn: ({ issueId, resolutionNotes }: { issueId: string; resolutionNotes?: string }) =>
      updateIssue(issueId, { status: 'solved', ...(resolutionNotes ? { resolutionNotes } : {}) }),
    onSuccess: () => {
      invalidate();
      setSolving(null);
      toast.show('Issue solved', { variant: 'success' });
    },
    onError: toastError('Could not solve the issue.'),
  });
  const dropIssueMutation = useMutation({
    mutationFn: (issueId: string) => updateIssue(issueId, { status: 'dropped' }),
    onSuccess: invalidate,
    onError: toastError('Could not drop the issue.'),
  });
  const issueToTodoMutation = useMutation({
    mutationFn: (issue: Issue) =>
      createTodo({
        title: issue.title,
        issueId: issue.id,
        meetingId: id,
        departmentId: workspaceQuery.data?.meeting.departmentId ?? undefined,
      }),
    onSuccess: () => {
      invalidate();
      toast.show('To-do created from the issue', { variant: 'success' });
    },
    onError: toastError('Could not create the to-do.'),
  });

  const ws = workspaceQuery.data;

  if (workspaceQuery.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="size-8 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
      </div>
    );
  }

  if (workspaceQuery.error || !ws) {
    return (
      <div className="mx-auto max-w-xl space-y-4">
        <Card className="flex items-start gap-3 border-kw-danger-text/30 bg-kw-danger-bg p-4">
          <AlertTriangle className="size-5 shrink-0 text-kw-danger-text" />
          <div>
            <p className="text-[14px] font-medium text-kw-danger-text">Couldn&apos;t load the meeting</p>
            <p className="mt-1 text-[13px] text-kw-danger-text/80">
              {workspaceQuery.error instanceof Error ? workspaceQuery.error.message : 'Unknown error'}
            </p>
          </div>
        </Card>
        <Button
          variant="secondary"
          onClick={() => {
            void navigate('/eos/meetings');
          }}
        >
          <ArrowLeft className="size-4" />
          Back to meetings
        </Button>
      </div>
    );
  }

  const meeting = ws.meeting;
  const agenda = ws.agenda;
  const isFacilitator =
    meeting.facilitatorMembershipId !== null && meeting.facilitatorMembershipId === myMembershipId;

  const header = (
    <div className="flex items-center gap-3">
      <button
        type="button"
        aria-label="Back to meetings"
        onClick={() => {
          void navigate('/eos/meetings');
        }}
        className="rounded p-1.5 text-kw-muted hover:bg-kw-bg hover:text-kw-shark"
      >
        <ArrowLeft className="size-5" />
      </button>
      <div className="min-w-0 flex-1">
        <h1 className="typo-h1">{meeting.seriesName ?? 'L10 meeting'}</h1>
        <p className="mt-0.5 text-[13px] text-kw-muted">{formatMeetingTime(meeting.scheduledFor)}</p>
      </div>
      {meeting.status === 'in_progress' ? <Badge variant="info">In progress</Badge> : null}
    </div>
  );

  if (meeting.status === 'concluded' || recap !== null) {
    return (
      <div className="mx-auto max-w-6xl space-y-4">
        {header}
        <RecapView
          meeting={meeting}
          recap={recap}
          onBack={() => {
            void navigate('/eos/meetings');
          }}
        />
      </div>
    );
  }

  if (meeting.status === 'scheduled') {
    return (
      <div className="mx-auto max-w-6xl space-y-4">
        {header}
        <Card className="mx-auto flex max-w-xl flex-col items-center gap-3 p-10 text-center">
          <Play className="size-10 text-kw-ghost" />
          <div>
            <p className="text-[15px] font-medium text-kw-shark">This L10 hasn&apos;t started yet</p>
            <p className="mt-1 text-[13px] text-kw-muted">
              Scheduled for {formatMeetingTime(meeting.scheduledFor)}. Whoever starts it becomes the
              facilitator.
            </p>
          </div>
          <Button
            isLoading={startMutation.isPending}
            onClick={() => {
              startMutation.mutate();
            }}
          >
            <Play className="size-4" />
            Start L10
          </Button>
        </Card>
      </div>
    );
  }

  if (meeting.status === 'canceled') {
    return (
      <div className="mx-auto max-w-6xl space-y-4">
        {header}
        <Card className="mx-auto max-w-xl p-10 text-center">
          <p className="text-[15px] font-medium text-kw-shark">This meeting was canceled.</p>
        </Card>
      </div>
    );
  }

  // ── In progress — the actual runner ───────────────────────────────────
  const currentKey = meeting.currentSegment;
  const currentIndex = agenda.findIndex((s) => s.key === currentKey);
  const currentSegment = currentIndex >= 0 ? agenda[currentIndex] : undefined;
  const isLastSegment = currentIndex === agenda.length - 1;
  const segmentEndsAt =
    meeting.segmentStartedAt !== null && currentSegment !== undefined
      ? Date.parse(meeting.segmentStartedAt) + currentSegment.minutes * 60_000
      : null;
  const remainingMs = segmentEndsAt !== null ? segmentEndsAt - now : null;

  function renderPane(): ReactNode {
    if (!ws) return null;
    switch (currentKey) {
      case 'segue':
        return (
          <SeguePane
            ws={ws}
            isFacilitator={isFacilitator}
            pending={attendeesMutation.isPending}
            onSetAttendees={(entries) => {
              attendeesMutation.mutate(entries);
            }}
          />
        );
      case 'scorecard':
        return <ScorecardPane metrics={ws.teamScorecard} />;
      case 'rocks':
        return (
          <RocksPane
            rocks={ws.rocks}
            onCycle={(rock) => {
              rockStatusMutation.mutate({ rockId: rock.id, rockStatus: NEXT_ROCK_STATUS[rock.status] });
            }}
          />
        );
      case 'headlines':
        return (
          <HeadlinesPane
            headlines={ws.headlines}
            adding={addHeadlineMutation.isPending}
            onAdd={(title) => {
              addHeadlineMutation.mutate(title);
            }}
            canArchive={(h) =>
              isSuper || (h.authorMembershipId !== null && h.authorMembershipId === myMembershipId)
            }
            onArchive={(headlineId) => {
              archiveHeadlineMutation.mutate(headlineId);
            }}
          />
        );
      case 'todos':
        return (
          <TodosPane
            ws={ws}
            onComplete={(todoId) => {
              completeTodoMutation.mutate(todoId);
            }}
            onCarryOver={(todoId) => {
              carryOverMutation.mutate(todoId);
            }}
          />
        );
      case 'ids':
        return (
          <IdsPane
            issues={ws.rankedIssues}
            onSolve={setSolving}
            onDrop={(issueId) => {
              dropIssueMutation.mutate(issueId);
            }}
            onTodo={(issue) => {
              issueToTodoMutation.mutate(issue);
            }}
          />
        );
      case 'conclude':
        return (
          <ConcludePane
            ws={ws}
            myMembershipId={myMembershipId}
            isFacilitator={isFacilitator}
            ratingPending={ratingMutation.isPending}
            onRate={(rating) => {
              ratingMutation.mutate(rating);
            }}
            concludePending={concludeMutation.isPending}
            onConclude={(summaryText) => {
              concludeMutation.mutate(summaryText);
            }}
          />
        );
      default:
        return (
          <Card className="p-10 text-center">
            <p className="text-[15px] font-medium text-kw-shark">
              {currentSegment?.label ?? 'Off the agenda'}
            </p>
            <p className="mt-1 text-[13px] text-kw-muted">
              Nothing to display for this segment — run it out loud.
            </p>
          </Card>
        );
    }
  }

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      {header}

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        {/* Agenda rail */}
        <aside className="w-full shrink-0 lg:w-64">
          <Card className="p-2">
            <ol className="space-y-1">
              {agenda.map((segment, index) => {
                const state =
                  segment.key === currentKey ? 'current' : index < currentIndex ? 'done' : 'upcoming';
                const row = (
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        'grid size-5 shrink-0 place-items-center rounded-full text-[11px] font-medium',
                        state === 'done' && 'bg-kw-emerald-light text-kw-tag-text',
                        state === 'current' && 'bg-kw-cerulean text-white',
                        state === 'upcoming' && 'bg-kw-bg text-kw-muted',
                      )}
                    >
                      {state === 'done' ? <Check className="size-3" /> : index + 1}
                    </span>
                    <span
                      className={cn(
                        'flex-1 truncate text-[13px]',
                        state === 'current' ? 'font-medium text-kw-shark' : 'text-kw-muted',
                      )}
                    >
                      {segment.label}
                    </span>
                    {state === 'current' && remainingMs !== null ? (
                      <span
                        className={cn(
                          'font-mono text-[12px] font-medium tabular-nums',
                          remainingMs < 0 ? 'text-kw-danger-text' : 'text-kw-shark',
                        )}
                      >
                        {remainingMs < 0 ? `-${formatClock(remainingMs)}` : formatClock(remainingMs)}
                      </span>
                    ) : (
                      <span className="text-[11px] text-kw-muted">{segment.minutes}m</span>
                    )}
                  </div>
                );
                return (
                  <li key={segment.key}>
                    {isFacilitator && state !== 'current' ? (
                      <button
                        type="button"
                        aria-label={`Jump to ${segment.label}`}
                        disabled={advanceMutation.isPending}
                        onClick={() => {
                          advanceMutation.mutate(segment.key);
                        }}
                        className="w-full rounded-kw-md px-2 py-1.5 text-left hover:bg-kw-bg disabled:opacity-50"
                      >
                        {row}
                      </button>
                    ) : (
                      <div
                        className={cn(
                          'rounded-kw-md px-2 py-1.5',
                          state === 'current' && 'bg-kw-cerulean-light',
                        )}
                      >
                        {row}
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>

            {isFacilitator ? (
              <div className="mt-2 space-y-2 border-t border-kw-border p-2 pt-3">
                <Button
                  size="sm"
                  className="w-full"
                  disabled={isLastSegment}
                  isLoading={advanceMutation.isPending}
                  onClick={() => {
                    advanceMutation.mutate(undefined);
                  }}
                >
                  Advance
                  <ArrowRight className="size-3.5" />
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  className="w-full"
                  isLoading={concludeMutation.isPending}
                  onClick={() => {
                    concludeMutation.mutate(undefined);
                  }}
                >
                  Conclude meeting
                </Button>
              </div>
            ) : (
              <p className="border-t border-kw-border p-2 pt-3 text-center text-[11px] text-kw-muted">
                The facilitator drives the agenda.
              </p>
            )}
          </Card>
        </aside>

        {/* Segment pane */}
        <main className="min-w-0 flex-1 space-y-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="typo-h3">{currentSegment?.label ?? 'Agenda'}</h2>
            {currentSegment ? (
              <p className="text-[12px] text-kw-muted">Timebox: {currentSegment.minutes} min</p>
            ) : null}
          </div>
          {renderPane()}
        </main>
      </div>

      {/* Solve-issue modal (IDS) */}
      <Modal
        open={solving !== null}
        onClose={() => {
          setSolving(null);
        }}
        title="Mark solved"
        size="sm"
      >
        {solving ? (
          <SolveIssueForm
            issue={solving}
            isPending={solveIssueMutation.isPending}
            onSubmit={(resolutionNotes) => {
              solveIssueMutation.mutate({
                issueId: solving.id,
                ...(resolutionNotes ? { resolutionNotes } : {}),
              });
            }}
            onCancel={() => {
              setSolving(null);
            }}
          />
        ) : null}
      </Modal>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Segment panes
// ─────────────────────────────────────────────────────────────────────────

function SeguePane({
  ws,
  isFacilitator,
  pending,
  onSetAttendees,
}: {
  ws: MeetingWorkspace;
  isFacilitator: boolean;
  pending: boolean;
  onSetAttendees: (entries: AttendanceEntry[]) => void;
}) {
  const attendees = ws.meeting.attendees;
  const [addId, setAddId] = useState('');

  const employeesQuery = useQuery({
    queryKey: ['employees', 'directory'],
    queryFn: () => listEmployees({ status: 'active', limit: 500 }),
    enabled: isFacilitator,
  });
  const addable = useMemo(() => {
    const existing = new Set(attendees.map((a) => a.membershipId));
    return (employeesQuery.data?.data ?? []).filter(
      (e) => e.membershipId !== null && !existing.has(e.membershipId),
    );
  }, [employeesQuery.data, attendees]);

  const entries = (): AttendanceEntry[] =>
    attendees.map((a) => ({ membershipId: a.membershipId, present: a.present }));

  return (
    <div className="space-y-4">
      <Card className="flex items-start gap-3 border-kw-cerulean/20 bg-kw-cerulean-light p-4">
        <Sparkles className="mt-0.5 size-5 shrink-0 text-kw-cerulean-dark" />
        <p className="text-[13px] text-kw-cerulean-dark">
          Share your good news — one personal and one professional best from the week. Around the room,
          everyone goes.
        </p>
      </Card>

      <Card className="p-4">
        <h3 className="mb-2 text-[13px] font-medium uppercase tracking-wide text-kw-muted">Attendance</h3>
        {attendees.length === 0 ? (
          <p className="text-[13px] text-kw-muted">No one on the attendee sheet yet.</p>
        ) : (
          <ul className="space-y-1.5">
            {attendees.map((a) => (
              <li key={a.id} className="flex items-center gap-2">
                {isFacilitator ? (
                  <input
                    type="checkbox"
                    aria-label={`Present: ${a.name}`}
                    checked={a.present}
                    disabled={pending}
                    onChange={(e) => {
                      onSetAttendees(
                        entries().map((entry) =>
                          entry.membershipId === a.membershipId
                            ? { ...entry, present: e.target.checked }
                            : entry,
                        ),
                      );
                    }}
                    className="size-4 rounded border-kw-border text-kw-cerulean focus:ring-kw-cerulean/40"
                  />
                ) : (
                  <CheckCircle2 className={`size-4 ${a.present ? 'text-kw-tag-text' : 'text-kw-ghost'}`} />
                )}
                <span className={`text-[13px] ${a.present ? 'text-kw-shark' : 'text-kw-muted'}`}>
                  {a.name}
                </span>
              </li>
            ))}
          </ul>
        )}

        {isFacilitator ? (
          <form
            onSubmit={(e: FormEvent<HTMLFormElement>) => {
              e.preventDefault();
              if (!addId) return;
              onSetAttendees([...entries(), { membershipId: addId, present: true }]);
              setAddId('');
            }}
            className="mt-3 flex items-center gap-2 border-t border-kw-border pt-3"
          >
            <select
              aria-label="Add attendee"
              value={addId}
              onChange={(e) => {
                setAddId(e.target.value);
              }}
              className="flex-1 rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
            >
              <option value="">Add an attendee…</option>
              {addable.map((e) => (
                <option key={e.id} value={e.membershipId ?? ''}>
                  {e.firstName} {e.lastName}
                </option>
              ))}
            </select>
            <Button type="submit" size="sm" variant="secondary" disabled={!addId} isLoading={pending}>
              Add
            </Button>
          </form>
        ) : null}
      </Card>
    </div>
  );
}

function ScorecardPane({ metrics }: { metrics: WorkspaceMetric[] }) {
  if (metrics.length === 0) {
    return (
      <Card className="p-10 text-center">
        <p className="text-[15px] font-medium text-kw-shark">No metrics for this team yet</p>
        <p className="mt-1 text-[13px] text-kw-muted">Set them up on the Scorecards page.</p>
      </Card>
    );
  }
  return (
    <Card className="overflow-x-auto p-0">
      <table className="w-full min-w-[560px] text-left">
        <thead>
          <tr className="border-b border-kw-border text-[11px] uppercase tracking-wide text-kw-muted">
            <th className="px-4 py-2 font-medium">Metric</th>
            <th className="px-2 py-2 text-right font-medium">Goal</th>
            <th colSpan={4} className="px-2 py-2 text-center font-medium">
              Last 4 entries (newest right)
            </th>
          </tr>
        </thead>
        <tbody>
          {metrics.map((metric) => {
            // Entries arrive newest-first; render oldest → newest.
            const cells = [...metric.entries].reverse();
            const slots = Array.from({ length: 4 }, (_, i) => cells[cells.length - 4 + i] ?? null);
            return (
              <tr key={metric.id} className="border-b border-kw-border last:border-0">
                <td className="px-4 py-2 text-[13px] font-medium text-kw-shark">{metric.title}</td>
                <td className="px-2 py-2 text-right text-[12px] text-kw-muted">{metric.goal}</td>
                {slots.map((entry, i) => (
                  <td key={entry?.id ?? `empty-${String(i)}`} className="px-1 py-1.5 text-center">
                    {entry ? (
                      <span
                        className={cn(
                          'inline-block min-w-14 rounded px-2 py-1 text-[12px] font-medium',
                          METRIC_STATUS_TONE[entry.status],
                        )}
                      >
                        {entry.value}
                      </span>
                    ) : (
                      <span className="text-[12px] text-kw-ghost">—</span>
                    )}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}

function RocksPane({ rocks, onCycle }: { rocks: Rock[]; onCycle: (rock: Rock) => void }) {
  if (rocks.length === 0) {
    return (
      <Card className="p-10 text-center">
        <p className="text-[15px] font-medium text-kw-shark">No rocks for this quarter</p>
        <p className="mt-1 text-[13px] text-kw-muted">Set them on the Rocks page.</p>
      </Card>
    );
  }
  return (
    <Card className="p-0">
      <ul className="divide-y divide-kw-border">
        {rocks.map((rock) => {
          const done = rock.milestones.filter((m) => m.completedAt !== null).length;
          return (
            <li key={rock.id} className="flex items-start gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="text-[14px] font-medium text-kw-shark">{rock.title}</p>
                <p className="mt-0.5 text-[12px] capitalize text-kw-muted">
                  {rock.type} · {done}/{rock.milestones.length} milestones
                </p>
              </div>
              <button
                type="button"
                aria-label={`Change status of ${rock.title} (now ${ROCK_STATUS_META[rock.status].label})`}
                title="Click to cycle status"
                onClick={() => {
                  onCycle(rock);
                }}
                className="rounded-kw-full transition-opacity hover:opacity-80"
              >
                <RockStatusBadge status={rock.status} />
              </button>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function HeadlinesPane({
  headlines,
  adding,
  onAdd,
  canArchive,
  onArchive,
}: {
  headlines: Headline[];
  adding: boolean;
  onAdd: (title: string) => void;
  canArchive: (h: Headline) => boolean;
  onArchive: (headlineId: string) => void;
}) {
  const [title, setTitle] = useState('');
  return (
    <div className="space-y-4">
      <form
        onSubmit={(e: FormEvent<HTMLFormElement>) => {
          e.preventDefault();
          const trimmed = title.trim();
          if (!trimmed) return;
          onAdd(trimmed);
          setTitle('');
        }}
        className="flex items-center gap-2"
      >
        <input
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
          }}
          placeholder="Share a headline — customer wins, people news…"
          aria-label="New headline"
          className="flex-1 rounded-kw-md border border-kw-border bg-white px-3.5 py-2.5 text-[14px] text-kw-shark placeholder:text-kw-muted focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
        />
        <Button type="submit" size="sm" isLoading={adding} disabled={!title.trim()}>
          Share
        </Button>
      </form>

      {headlines.length === 0 ? (
        <Card className="flex flex-col items-center gap-3 p-10 text-center">
          <Megaphone className="size-10 text-kw-ghost" />
          <p className="text-[13px] text-kw-muted">No headlines yet — share the good news.</p>
        </Card>
      ) : (
        <Card className="p-0">
          <ul className="divide-y divide-kw-border">
            {headlines.map((h) => (
              <li key={h.id} className="flex items-center gap-3 px-4 py-3">
                <p className="min-w-0 flex-1 text-[14px] text-kw-shark">{h.title}</p>
                <span className="shrink-0 text-[11px] text-kw-muted">
                  {new Date(h.createdAt).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                  })}
                </span>
                {canArchive(h) ? (
                  <button
                    type="button"
                    aria-label={`Archive headline ${h.title}`}
                    onClick={() => {
                      onArchive(h.id);
                    }}
                    className="rounded p-1 text-kw-muted hover:bg-kw-bg hover:text-kw-danger-text"
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

function TodosPane({
  ws,
  onComplete,
  onCarryOver,
}: {
  ws: MeetingWorkspace;
  onComplete: (todoId: string) => void;
  onCarryOver: (todoId: string) => void;
}) {
  const todos = ws.openTodos;
  if (todos.length === 0) {
    return (
      <Card className="p-10 text-center">
        <p className="text-[15px] font-medium text-kw-shark">Nothing open</p>
        <p className="mt-1 text-[13px] text-kw-muted">Every to-do from last week is done.</p>
      </Card>
    );
  }
  const nowMs = Date.now();
  return (
    <Card className="p-0">
      <ul className="divide-y divide-kw-border">
        {todos.map((t) => {
          const overdue = Date.parse(t.dueDate) < nowMs;
          const owner = t.ownerMembership
            ? `${t.ownerMembership.user.firstName} ${t.ownerMembership.user.lastName}`.trim()
            : 'Unassigned';
          return (
            <li key={t.id} className="flex items-center gap-3 px-4 py-3">
              <input
                type="checkbox"
                aria-label={`Complete ${t.title}`}
                checked={t.completedAt !== null}
                onChange={() => {
                  onComplete(t.id);
                }}
                className="size-4 rounded border-kw-border text-kw-cerulean focus:ring-kw-cerulean/40"
              />
              <div className="min-w-0 flex-1">
                <p className="text-[14px] text-kw-shark">{t.title}</p>
                <p className="mt-0.5 text-[12px] text-kw-muted">
                  {owner} ·{' '}
                  <span className={overdue ? 'font-medium text-kw-danger-text' : undefined}>
                    due{' '}
                    {new Date(t.dueDate).toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                    })}
                  </span>
                </p>
              </div>
              {t.carryCount > 0 ? <Badge variant="warning">Carried ×{t.carryCount}</Badge> : null}
              <button
                type="button"
                aria-label={`Carry over ${t.title}`}
                title="Push +7 days"
                onClick={() => {
                  onCarryOver(t.id);
                }}
                className="rounded p-1 text-kw-muted hover:bg-kw-bg hover:text-kw-shark"
              >
                <RefreshCw className="size-3.5" />
              </button>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function IdsPane({
  issues,
  onSolve,
  onDrop,
  onTodo,
}: {
  issues: Issue[];
  onSolve: (issue: Issue) => void;
  onDrop: (issueId: string) => void;
  onTodo: (issue: Issue) => void;
}) {
  if (issues.length === 0) {
    return (
      <Card className="p-10 text-center">
        <p className="text-[15px] font-medium text-kw-shark">The issues list is clear</p>
        <p className="mt-1 text-[13px] text-kw-muted">Nothing to IDS — use the time to get ahead.</p>
      </Card>
    );
  }
  return (
    <Card className="p-0">
      <ul className="divide-y divide-kw-border">
        {issues.map((issue, index) => (
          <li key={issue.id} className="flex items-start gap-3 px-4 py-3">
            <span className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-kw-bg text-[11px] font-medium text-kw-muted">
              {index + 1}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[14px] font-medium text-kw-shark">{issue.title}</p>
              {issue.description ? (
                <p className="mt-0.5 text-[13px] text-kw-muted">{issue.description}</p>
              ) : null}
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  onSolve(issue);
                }}
              >
                Solve
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  onDrop(issue.id);
                }}
              >
                Drop
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={issue.promotedToTodoId !== null}
                aria-label={`Create a to-do from ${issue.title}`}
                onClick={() => {
                  onTodo(issue);
                }}
              >
                <ArrowUpRight className="size-3.5" />
                To-do
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function ConcludePane({
  ws,
  myMembershipId,
  isFacilitator,
  ratingPending,
  onRate,
  concludePending,
  onConclude,
}: {
  ws: MeetingWorkspace;
  myMembershipId: string | null;
  isFacilitator: boolean;
  ratingPending: boolean;
  onRate: (rating: number) => void;
  concludePending: boolean;
  onConclude: (summaryText?: string) => void;
}) {
  const meeting = ws.meeting;
  const [summary, setSummary] = useState('');
  const myAttendee = meeting.attendees.find((a) => a.membershipId === myMembershipId) ?? null;
  const createdCount = ws.openTodos.filter((t) => t.meetingId === meeting.id).length;
  const ratedCount = meeting.attendees.filter((a) => a.rating !== null).length;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <Card className="p-4 text-center">
          <p className="text-[22px] font-semibold text-kw-shark">{createdCount}</p>
          <p className="mt-1 text-[12px] text-kw-muted">To-dos captured</p>
        </Card>
        <Card className="p-4 text-center">
          <p className="text-[22px] font-semibold text-kw-shark">{ws.rankedIssues.length}</p>
          <p className="mt-1 text-[12px] text-kw-muted">Issues still open</p>
        </Card>
        <Card className="p-4 text-center">
          <p className="text-[22px] font-semibold text-kw-shark">
            {ratedCount}/{meeting.attendees.length}
          </p>
          <p className="mt-1 text-[12px] text-kw-muted">Ratings in</p>
        </Card>
      </div>

      <Card className="p-4">
        <h3 className="text-[13px] font-medium uppercase tracking-wide text-kw-muted">Rate this meeting</h3>
        {myAttendee ? (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
              <button
                key={n}
                type="button"
                aria-label={`Rate ${String(n)} out of 10`}
                disabled={ratingPending}
                onClick={() => {
                  onRate(n);
                }}
                className={cn(
                  'size-9 rounded-kw-md border text-[13px] font-medium transition-colors',
                  myAttendee.rating === n
                    ? 'border-kw-cerulean bg-kw-cerulean text-white'
                    : 'border-kw-border bg-white text-kw-shark hover:bg-kw-bg',
                )}
              >
                {n}
              </button>
            ))}
          </div>
        ) : (
          <p className="mt-2 text-[13px] text-kw-muted">
            Only attendees rate the meeting — ask the facilitator to add you to the sheet.
          </p>
        )}
      </Card>

      {isFacilitator ? (
        <Card className="space-y-3 p-4">
          <label className="flex flex-col gap-1">
            <span className="text-[13px] font-medium text-kw-label">Summary (optional)</span>
            <textarea
              value={summary}
              onChange={(e) => {
                setSummary(e.target.value);
              }}
              rows={2}
              placeholder="Decisions, cascading messages…"
              className="rounded-kw-md border border-kw-border bg-white px-3.5 py-2.5 text-[14px] text-kw-shark placeholder:text-kw-muted focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
            />
          </label>
          <p className="text-[12px] text-kw-muted">
            Concluding closes the meeting and carries every open meeting to-do a week forward.
          </p>
          <div className="flex justify-end">
            <Button
              isLoading={concludePending}
              onClick={() => {
                onConclude(summary.trim() || undefined);
              }}
            >
              Conclude meeting
            </Button>
          </div>
        </Card>
      ) : null}
    </div>
  );
}

function SolveIssueForm({
  issue,
  isPending,
  onSubmit,
  onCancel,
}: {
  issue: Issue;
  isPending: boolean;
  onSubmit: (resolutionNotes: string) => void;
  onCancel: () => void;
}) {
  const [notes, setNotes] = useState('');
  return (
    <form
      onSubmit={(e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        onSubmit(notes.trim());
      }}
      className="space-y-4"
    >
      <p className="text-[13px] text-kw-shark">
        Mark <strong>{issue.title}</strong> as solved?
      </p>
      <label className="flex flex-col gap-1">
        <span className="text-[13px] font-medium text-kw-label">Resolution notes (optional)</span>
        <textarea
          value={notes}
          onChange={(e) => {
            setNotes(e.target.value);
          }}
          rows={3}
          placeholder="What was decided?"
          className="rounded-kw-md border border-kw-border bg-white px-3.5 py-2.5 text-[14px] text-kw-shark placeholder:text-kw-muted focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
        />
      </label>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" isLoading={isPending}>
          Mark solved
        </Button>
      </div>
    </form>
  );
}

function RecapView({
  meeting,
  recap,
  onBack,
}: {
  meeting: WorkspaceMeeting;
  recap: MeetingRecap | null;
  onBack: () => void;
}) {
  const average = recap?.ratings.average ?? meeting.averageRating;
  return (
    <Card className="mx-auto flex max-w-xl flex-col items-center gap-4 p-8 text-center">
      <CheckCircle2 className="size-10 text-kw-tag-text" />
      <div>
        <h2 className="typo-h3">Meeting concluded</h2>
        {average !== null ? (
          <p className="mt-2 text-[28px] font-semibold text-kw-shark">
            {average.toFixed(1)}
            <span className="text-[15px] font-normal text-kw-muted">/10</span>
          </p>
        ) : (
          <p className="mt-2 text-[13px] text-kw-muted">No ratings were submitted.</p>
        )}
      </div>

      {recap ? (
        <dl className="grid w-full grid-cols-3 gap-3">
          <div className="rounded-kw-md bg-kw-bg p-3">
            <dt className="text-[11px] uppercase tracking-wide text-kw-muted">To-dos created</dt>
            <dd className="mt-1 text-[18px] font-semibold text-kw-shark">{recap.todosCreatedCount}</dd>
          </div>
          <div className="rounded-kw-md bg-kw-bg p-3">
            <dt className="text-[11px] uppercase tracking-wide text-kw-muted">Issues solved</dt>
            <dd className="mt-1 text-[18px] font-semibold text-kw-shark">{recap.issuesSolvedCount}</dd>
          </div>
          <div className="rounded-kw-md bg-kw-bg p-3">
            <dt className="text-[11px] uppercase tracking-wide text-kw-muted">Carried over</dt>
            <dd className="mt-1 text-[18px] font-semibold text-kw-shark">{recap.todosCarried.length}</dd>
          </div>
        </dl>
      ) : null}

      {meeting.summaryText ? <p className="text-[13px] italic text-kw-muted">{meeting.summaryText}</p> : null}

      <Button onClick={onBack}>
        <ArrowLeft className="size-4" />
        Back to meetings
      </Button>
    </Card>
  );
}
